/**
 * Marketing Terminal — Brand Kit display
 *
 * Read-only view of the active brand_kit + voice_corpus. Confirms what was
 * loaded from your three brand documents (Brand Identity Brief, Brand Style
 * Guide v4, Why We Built Coach Cedar v3) by migration 1359.
 *
 * This page is the source of truth display — Cowork uses identical data via
 * /api/admin/marketing/agent/context for every drafting task.
 *
 * Backend: GET /api/admin/marketing/brand-kit/active?include_corpus=true
 */

import React from 'react';
import { Quote, AlertOctagon, Palette, Megaphone, Heart, Library, Type } from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';

// ── Types ────────────────────────────────────────────────────────────────────

interface VoiceTrait { key: string; name: string; description: string; sample: string; }
interface Pillar { key: string; name: string; description: string; sample_message: string; }
interface ForbiddenWord { word: string; replacement: string; reason: string; }
interface LockedPhrase { key: string; text: string; usage: string; }
interface CompetitiveFraming {
  rules?: string[];
  approved_status_quo_framing?: string[];
  if_directly_asked_about_competitors?: string;
}
interface PaletteColor { hex: string; role?: string; usage_pct?: number; opacity?: number; }
interface FontDef { family: string; fallback?: string; weights: number[]; usage: string; }

interface VoiceCorpusEntry {
  id: string;
  sample_type: string;
  title: string;
  body: string;
  annotations: string[];
  source_document: string;
  is_canonical: boolean;
}

interface BrandKit {
  id: string;
  version_number: number;
  name: string;
  brand_name: string;
  domain: string;
  legal_entity_name?: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  text_color: string;
  bg_color: string;
  palette: Record<string, PaletteColor>;
  fonts: { body?: FontDef; display?: FontDef };
  photography_style: string;
  tone_descriptors: string[];
  voice_traits: VoiceTrait[];
  forbidden_words: ForbiddenWord[];
  locked_phrases: LockedPhrase[];
  competitive_framing: CompetitiveFraming;
  pillars: Pillar[];
  activated_at: string;
  voice_corpus?: VoiceCorpusEntry[];
}

interface Envelope<T> { success: boolean; data: T; }

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5 * 60_000, retry: 1 } },
});

// 2026-05-21 ordering fix. The palette field is stored as Postgres
// `jsonb`, which re-sorts keys at storage time by length-then-alphabetical
// instead of preserving the insertion order from the seed migrations
// (1383 / 1398). That made the rendered palette open with `info` →
// `error` → `success` → `warning` (the four shortest keys), burying the
// actual core brand colors (cedar_prime, cedar_ink, dark_cedar, …) in
// the middle of the grid.
//
// This array lets us iterate in a deliberate brand-led order. Unknown
// keys (added in a future seed migration that this constant hasn't been
// updated for yet) fall through alphabetically at the end so new colors
// are still visible — the gate test
// `tests/gate/test_admin_brand_kit_palette_order.py` catches that
// case and tells the editor to fold the new key in.
//
// Source of truth for the key set: db/migrations/1398_brand_kit_v1_respawn.sql.
const PALETTE_ORDER: readonly string[] = [
  // Core brand (the daily-driver colors — open with these).
  'cedar_prime',     // 60% usage — the signature green
  'cedar_ink',       //  5% usage — body text / icons
  'dark_cedar',      // 10% usage — depth accents
  'cedar_light',     // buttons, success states
  'cedar_stream',    // 15% usage — coach signal
  'cedar_mist',      // soft accents
  'cedar_subtle',    // user message bubbles
  'limestone',       // 20% usage — page backgrounds
  // Stream interaction states (paired with cedar_stream).
  'stream_hover',
  'stream_pressed',
  // Activity ring colors (grouped — cardio / strength / hiit / mobility / rest).
  'activity_cardio',
  'activity_strength',
  'activity_hiit',
  'activity_mobility',
  'activity_rest',
  // Functional / system colors last (these are the shortest keys, which
  // is exactly why jsonb storage order surfaced them first — invert that).
  'success',
  'info',
  'warning',
  'error',
];

/**
 * Order palette entries for display. Keys appear in PALETTE_ORDER order;
 * any key not in PALETTE_ORDER falls through at the end, alphabetically.
 *
 * Why not just sort by the array index alone: forward-compat. If a future
 * seed migration adds a new color (say `cedar_glow`), we render it
 * instead of dropping it on the floor — but it shows up at the bottom
 * until someone slots it into PALETTE_ORDER. The gate test pins this
 * contract.
 */
function orderedPaletteEntries(
  palette: Record<string, PaletteColor>
): Array<[string, PaletteColor]> {
  const known: Array<[string, PaletteColor]> = [];
  const unknown: Array<[string, PaletteColor]> = [];
  const orderIndex = new Map(PALETTE_ORDER.map((k, i) => [k, i]));

  for (const entry of Object.entries(palette)) {
    if (orderIndex.has(entry[0])) {
      known.push(entry);
    } else {
      unknown.push(entry);
    }
  }
  known.sort((a, b) => orderIndex.get(a[0])! - orderIndex.get(b[0])!);
  unknown.sort((a, b) => a[0].localeCompare(b[0]));
  return [...known, ...unknown];
}

export function BrandKitPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrandKitInner />
    </QueryClientProvider>
  );
}

function BrandKitInner() {
  const { data, isLoading, error } = useQuery<Envelope<BrandKit>>({
    queryKey: ['marketing-brand-kit-full'],
    queryFn: () =>
      adminFetch<Envelope<BrandKit>>('/api/admin/marketing/brand-kit/active?include_corpus=true'),
  });

  if (isLoading) return <div className="text-sm text-gray-500">Loading brand kit...</div>;
  if (error) return <div className="text-sm text-red-600">Failed to load brand kit. Run migration 1383 (brand_kit_v1_seed) — see scripts/bulletproof_migration_runner.py for how the runner tracks applied migrations by filename.</div>;

  const b = data?.data;
  if (!b) return null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--cedar-ink)' }}>
            Brand Kit
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Active version: <strong>{b.name}</strong> ({b.brand_name}) ·
            domain {b.domain} ·
            activated {new Date(b.activated_at).toLocaleDateString()}
          </p>
        </div>
        <span
          className="inline-block rounded px-2 py-1 text-[10px] font-medium uppercase"
          style={{ backgroundColor: 'var(--cedar-subtle)', color: 'var(--cedar-primary)' }}
        >
          v{b.version_number} · active
        </span>
      </div>

      {/* Visual identity */}
      <Section icon={Palette} title="Visual Identity">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Palette</h3>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          {orderedPaletteEntries(b.palette ?? {}).map(([key, col]) => (
            <div key={key} className="rounded-lg border border-gray-100 bg-white p-2">
              <div
                className="mb-2 h-12 w-full rounded"
                style={{ backgroundColor: col.hex, opacity: col.opacity ?? 1 }}
              />
              <div className="text-xs font-semibold text-gray-900">{key.replace(/_/g, ' ')}</div>
              <div className="text-[10px] text-gray-500">{col.hex}</div>
              {col.role && <div className="mt-1 text-[10px] text-gray-400">{col.role}</div>}
              {col.usage_pct && (
                <div className="mt-1 text-[10px] font-medium" style={{ color: 'var(--cedar-primary)' }}>
                  {col.usage_pct}% target usage
                </div>
              )}
            </div>
          ))}
        </div>

        <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Typography
        </h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {Object.entries(b.fonts ?? {}).map(([role, font]) => (
            <div key={role} className="rounded-lg border border-gray-100 bg-white p-3">
              <div className="text-xs font-semibold text-gray-500 uppercase">{role}</div>
              <div className="mt-1 text-2xl" style={{ fontFamily: font.family }}>
                {font.family}
              </div>
              <div className="text-[10px] text-gray-500">
                Weights: {font.weights.join(', ')}
              </div>
              <div className="mt-1 text-[11px] text-gray-600">{font.usage}</div>
            </div>
          ))}
        </div>

        {b.photography_style && (
          <>
            <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Photography style (image-gen prompt fragment)
            </h3>
            <div className="rounded-lg border border-gray-100 bg-white p-3 text-xs italic text-gray-700">
              {b.photography_style}
            </div>
          </>
        )}
      </Section>

      {/* Voice */}
      <Section icon={Megaphone} title="Voice & Tone">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Tone descriptors
        </h3>
        <div className="mb-4 flex flex-wrap gap-2">
          {b.tone_descriptors?.map(t => (
            <span
              key={t}
              className="rounded-full px-3 py-1 text-xs font-medium"
              style={{ backgroundColor: 'var(--cedar-subtle)', color: 'var(--cedar-dark)' }}
            >
              {t}
            </span>
          ))}
        </div>

        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          7 Rock-Steady Traits
        </h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {b.voice_traits?.map(trait => (
            <div key={trait.key} className="rounded-lg border border-gray-100 bg-white p-3">
              <div className="text-sm font-bold" style={{ color: 'var(--cedar-primary)' }}>
                {trait.name}
              </div>
              <div className="mt-1 text-xs text-gray-600">{trait.description}</div>
              <div className="mt-2 rounded bg-gray-50 px-2 py-1.5 text-xs italic text-gray-700">
                <Quote className="mr-1 inline h-3 w-3 text-gray-400" />
                {trait.sample}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Pillars */}
      <Section icon={Heart} title="5 Value Proposition Pillars">
        <div className="grid gap-3 lg:grid-cols-5">
          {b.pillars?.map(p => (
            <div
              key={p.key}
              className="rounded-lg border-2 border-gray-100 bg-white p-3"
              style={{ borderTopColor: 'var(--cedar-primary)', borderTopWidth: '4px' }}
            >
              <div className="text-sm font-bold text-gray-900">{p.name}</div>
              <div className="mt-1 text-xs text-gray-600">{p.description}</div>
              <div className="mt-2 text-xs italic text-gray-500">
                "{p.sample_message}"
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Locked phrases */}
      <Section icon={Type} title="Locked Phrases">
        <div className="space-y-2">
          {b.locked_phrases?.map(p => (
            <div key={p.key} className="rounded-lg border border-gray-100 bg-white p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                {p.key.replace(/_/g, ' ')}
              </div>
              <blockquote
                className="my-1 border-l-4 pl-3 text-sm font-medium text-gray-900"
                style={{ borderColor: 'var(--cedar-primary)' }}
              >
                "{p.text}"
              </blockquote>
              <div className="text-[11px] italic text-gray-500">{p.usage}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Forbidden words */}
      <Section icon={AlertOctagon} title="Forbidden Words" iconColor="var(--status-error)">
        <div className="overflow-x-auto rounded-lg border border-gray-100 bg-white">
          <table className="w-full text-xs">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Don't say</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Use instead</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Why</th>
              </tr>
            </thead>
            <tbody>
              {b.forbidden_words?.map(w => (
                <tr key={w.word} className="border-b border-gray-100 last:border-0">
                  <td className="px-3 py-2 font-mono text-red-600">{w.word}</td>
                  <td className="px-3 py-2 font-medium text-gray-900">{w.replacement}</td>
                  <td className="px-3 py-2 text-gray-500">{w.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] italic text-gray-500">
          These are auto-flagged as must-fix by Quality Gate (§24.1). Pattern matching runs
          on every external-facing piece before approval.
        </p>
      </Section>

      {/* Competitive framing */}
      {b.competitive_framing?.rules && (
        <Section icon={AlertOctagon} title="Competitive Framing" iconColor="var(--status-warning)">
          <div className="space-y-3">
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Rules</h3>
              <ul className="list-disc space-y-1 pl-5 text-xs text-gray-700">
                {b.competitive_framing.rules.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
            {b.competitive_framing.approved_status_quo_framing && (
              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Approved status-quo framing
                </h3>
                <ul className="space-y-1 pl-5 text-xs italic text-gray-700">
                  {b.competitive_framing.approved_status_quo_framing.map((r, i) => (
                    <li key={i}>"{r}"</li>
                  ))}
                </ul>
              </div>
            )}
            {b.competitive_framing.if_directly_asked_about_competitors && (
              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  If directly asked about competitors
                </h3>
                <blockquote
                  className="border-l-4 pl-3 text-xs italic text-gray-700"
                  style={{ borderColor: 'var(--status-warning)' }}
                >
                  "{b.competitive_framing.if_directly_asked_about_competitors}"
                </blockquote>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Voice corpus */}
      {b.voice_corpus && b.voice_corpus.length > 0 && (
        <Section icon={Library} title={`Voice Corpus (${b.voice_corpus.length} samples)`}>
          <div className="space-y-3">
            {b.voice_corpus.map(s => (
              <details key={s.id} className="group rounded-lg border border-gray-100 bg-white">
                <summary className="cursor-pointer p-3 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">{s.title}</span>
                        {s.is_canonical && (
                          <span
                            className="rounded px-1.5 py-0.5 text-[10px] font-medium uppercase"
                            style={{ backgroundColor: 'var(--cedar-subtle)', color: 'var(--cedar-primary)' }}
                          >
                            canonical
                          </span>
                        )}
                        <span className="text-[10px] text-gray-400">
                          {s.sample_type}
                        </span>
                      </div>
                      {s.source_document && (
                        <div className="mt-0.5 text-[10px] text-gray-500">
                          source: {s.source_document}
                        </div>
                      )}
                    </div>
                  </div>
                </summary>
                <div className="border-t border-gray-100 p-3">
                  <pre className="whitespace-pre-wrap text-xs text-gray-700" style={{ fontFamily: 'inherit' }}>
                    {s.body}
                  </pre>
                  {s.annotations && s.annotations.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        Voice qualities
                      </div>
                      <ul className="mt-1 space-y-0.5 pl-4 text-[11px] text-gray-600">
                        {s.annotations.map((a, i) => (
                          <li key={i} style={{ listStyleType: 'square' }}>{a}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </details>
            ))}
          </div>
        </Section>
      )}

      {/* Footer note */}
      <div
        className="rounded-lg border-l-4 bg-white p-4 text-xs text-gray-700"
        style={{ borderLeftColor: 'var(--cedar-primary)', boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}
      >
        <strong>Brand resilience:</strong> every content piece (social_posts, ad_creatives,
        landing_pages, etc.) captures <code>brand_kit_id</code> at creation. When a rebrand
        ships as v2, historical content stays attributed to v1 — your audit trail survives.
        See master spec §19 + plan §19.7.
      </div>
    </div>
  );
}

// ── Section helper ───────────────────────────────────────────────────────────

function Section({
  icon: Icon,
  title,
  iconColor,
  children,
}: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  title: string;
  iconColor?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color: iconColor ?? 'var(--cedar-primary)' }} />
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export default BrandKitPage;
